import type { ReactNode } from 'react'
import {
  CheckboxButton as AriaCheckboxButton,
  CheckboxField as AriaCheckboxField,
  FieldError as AriaFieldError,
  Text,
  type CheckboxFieldProps as AriaCheckboxFieldProps,
} from 'react-aria-components'
import { Check, Minus } from 'lucide-react'

import type { FormFieldErrorMessage } from './form-field'
import { cn } from '@/shared/lib/cn'

export interface CheckboxProps
  extends Omit<
    AriaCheckboxFieldProps,
    'children' | 'className'
  > {
  label: ReactNode
  description?: ReactNode
  errorMessage?: FormFieldErrorMessage

  className?: string
  checkboxClassName?: string
  labelClassName?: string
}

export function Checkbox({
  label,
  description,
  errorMessage,
  className,
  checkboxClassName,
  labelClassName,
  isInvalid,
  validationBehavior = 'aria',
  ...checkboxProps
}: CheckboxProps) {
  const hasStaticError =
    typeof errorMessage === 'string' &&
    errorMessage.trim().length > 0

  const resolvedIsInvalid =
    isInvalid ?? hasStaticError

  return (
    <AriaCheckboxField
      {...checkboxProps}
      isInvalid={resolvedIsInvalid}
      validationBehavior={validationBehavior}
      className={({ isDisabled }) =>
        cn(
          'flex flex-col gap-1.5',
          isDisabled && 'opacity-60',
          className,
        )
      }
    >
      <AriaCheckboxButton
        className={({
          isFocusVisible,
          isDisabled,
        }) =>
          cn(
            [
              'group flex w-fit items-start gap-3',
              'rounded-md outline-none',
              'text-sm text-(--color-foreground)',
            ],
            isFocusVisible && [
              'ring-2 ring-(--color-primary)',
              'ring-offset-2',
              'ring-offset-(--color-surface)',
            ],
            isDisabled
              ? 'cursor-not-allowed'
              : 'cursor-pointer',
          )
        }
      >
        {({
          isSelected,
          isIndeterminate,
          isInvalid: checkboxIsInvalid,
        }) => (
          <>
            <span
              className={cn(
                [
                  'mt-0.5 flex h-5 w-5 shrink-0',
                  'items-center justify-center',
                  'rounded border',
                  'transition-colors',
                ],
                checkboxIsInvalid
                  ? 'border-(--color-danger)'
                  : 'border-(--color-border)',
                isSelected || isIndeterminate
                  ? [
                      'border-(--color-primary)',
                      'bg-(--color-primary)',
                      'text-white',
                    ]
                  : [
                      'bg-(--color-surface)',
                      'group-hover:border-(--color-primary)',
                    ],
                checkboxClassName,
              )}
            >
              {isIndeterminate ? (
                <Minus
                  aria-hidden="true"
                  className="h-3.5 w-3.5"
                  strokeWidth={3}
                />
              ) : null}

              {isSelected && !isIndeterminate ? (
                <Check
                  aria-hidden="true"
                  className="h-3.5 w-3.5"
                  strokeWidth={3}
                />
              ) : null}
            </span>

            <span
              className={cn(
                'leading-6',
                labelClassName,
              )}
            >
              {label}
            </span>
          </>
        )}
      </AriaCheckboxButton>

      {description ? (
        <Text
          slot="description"
          className={[
            'ml-8 text-xs leading-5',
            'text-(--color-muted-foreground)',
          ].join(' ')}
        >
          {description}
        </Text>
      ) : null}

      <AriaFieldError
        className={[
          'ml-8 text-xs leading-5',
          'text-(--color-danger)',
        ].join(' ')}
      >
        {errorMessage}
      </AriaFieldError>
    </AriaCheckboxField>
  )
}